import { getModuleOrDefault } from '@uhg-psx/mapping-files-translation-transperfect';
import enLangugages from '@uhg-psx/mapping-files-translation-transperfect/data/en/languageTranslations.json';
import { t } from 'i18next';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import { useLagoon } from '../../../hooks/useLagoon';
import { removeSpecialCharactersUsingMatch } from '../../../utils/smartChoice.utils';

export const getLanguagesSpokenValues = async (
  locale: string,
  pesLangs: string
): Promise<Array<{ title: string; value: string }>> => {
  const languagesValues = await getModuleOrDefault(
    locale,
    'languageTranslations'
  );

  const pesLangsFormatted =
    pesLangs?.split(',')?.map((lang: string) => lang.trim()?.toLowerCase()) ??
    [];
  return Object.keys(languagesValues).reduce((acc: any, key: string) => {
    if (pesLangsFormatted?.includes(key?.trim()?.toLowerCase())) {
      acc.push({ title: languagesValues[key], value: key });
    }
    return acc;
  }, []);
};

export const getSelectedLanguage = async (keys: string[], language: string) => {
  const languagesValues = await getModuleOrDefault(
    language,
    'languageTranslations'
  );

  return keys.map((key) => languagesValues[key] || enLangugages[key]);
};

export const useLanguageTranslations = (language: string) => {
  const [languagesMap, setLanguageMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchLanguageMap = async () => {
      try {
        const fetchedLanguageMap = await getModuleOrDefault(
          language,
          'languageTranslations'
        );
        setLanguageMap(
          Array.isArray(fetchedLanguageMap)
            ? Object.fromEntries(
                fetchedLanguageMap.map((item) => [item.key, item.value])
              )
            : fetchedLanguageMap
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching language translations:', error);
      }
    };

    fetchLanguageMap();
  }, [language]);

  return languagesMap;
};

export const getSelectedGender = (
  keys: string[],
  list: { value: string; title: string }[]
): string[] => {
  const selectedGender = keys.map((key) => {
    const index = list.findIndex((item) => item.value === key);
    if (index !== -1) {
      return list[index]?.title;
    }
    return '';
  });
  return selectedGender.filter((item) => item !== '');
};

/**
 * Removes common keys from the form fields object that should not be sent to the backend.
 * Returns a new object without mutating the input.
 *
 * @template T - The type of the form fields object
 * @param formFields - The form fields object to process
 * @returns A new object with common keys removed
 *
 * @example
 * ```typescript
 * const data = { distance: '5', createdDate: '2024-01-01', uuid: '123' };
 * const cleaned = deleteCommonKeys(data);
 * // Result: { distance: '5' }
 * ```
 */
export const deleteCommonKeys = <T extends Record<string, unknown>>(
  formFields: T
): Omit<
  T,
  | 'createdDate'
  | 'lastUpdatedDate'
  | 'memberRandomizedKey'
  | 'uuid'
  | 'hasSetPreferences'
  | 'hasDismissedCoachMark'
  | 'hasDismissedCoachMarkWeb'
  | 'hasDefaultPreferences'
  | 'hasDismissedPopover'
> => {
  const result = { ...formFields };
  delete (result as Record<string, unknown>).createdDate;
  delete (result as Record<string, unknown>).lastUpdatedDate;
  delete (result as Record<string, unknown>).memberRandomizedKey;
  delete (result as Record<string, unknown>).uuid;
  delete (result as Record<string, unknown>).hasSetPreferences;
  delete (result as Record<string, unknown>).hasDismissedCoachMark;
  delete (result as Record<string, unknown>).hasDismissedCoachMarkWeb;
  delete (result as Record<string, unknown>).hasDefaultPreferences;
  delete (result as Record<string, unknown>).hasDismissedPopover;

  return result;
};

/**
 * Counts the number of differences between two preference objects.
 * Ignores common system keys like createdDate, uuid, etc.
 *
 * @param oldValues - The original preference values
 * @param newValues - The new preference values
 * @returns The count of differences between the two objects
 *
 * @example
 * ```typescript
 * const oldData = { distance: '5', virtualApptAvbl: 'y' };
 * const newData = { distance: '10', virtualApptAvbl: 'y' };
 * const diff = countDifferences(oldData, newData); // Returns 1
 * ```
 */
export const countDifferences = (
  oldValues: Record<string, unknown>,
  newValues: Record<string, unknown>
): number => {
  const formattedOldValues = deleteCommonKeys(
    JSON.parse(JSON.stringify(oldValues))
  );
  const formattedNewValues = deleteCommonKeys(
    JSON.parse(JSON.stringify(newValues))
  );

  const allKeys = Object.keys(formattedOldValues);
  let differenceCount = 0;

  _.forEach(allKeys, (key) => {
    const oldValue = formattedOldValues[key];
    const newValue = formattedNewValues[key];

    if (oldValue !== newValue && typeof oldValue !== 'object') {
      differenceCount++;
    } else if (
      typeof oldValue === 'object' &&
      oldValue?.length !== newValue?.length
    ) {
      differenceCount++;
    } else if (typeof oldValue === 'object') {
      const difference = _.difference(oldValue, newValue);
      if (difference.length > 0) {
        differenceCount++;
      }
    }
  });

  return differenceCount;
};

export const PREFERENCE_FIELDS = [
  'distance',
  'virtualApptAvbl',
  'onlineApptSchdlAvbl',
  'evngApptAvbl',
  'wkndApptAvbl',
  'providerBoardCertStatus',
  'providerGender',
  'languagesSpoken',
] as const;

export interface PreferencesData {
  distance?: string | null;
  virtualApptAvbl?: boolean | string;
  onlineApptSchdlAvbl?: boolean | string;
  evngApptAvbl?: boolean | string;
  wkndApptAvbl?: boolean | string;
  providerBoardCertStatus?: boolean | string;
  providerGender?: string[] | string;
  languagesSpoken?: string[] | string;
  [key: string]: unknown;
}

/**
 * Counts the number of enabled preferences in a preferences object.
 * Handles special cases like distance (non-empty string) and arrays (non-empty).
 *
 * @param preferences - The preferences object to count from
 * @returns The number of enabled preferences
 *
 * @example
 * ```typescript
 * const prefs = { distance: '5', virtualApptAvbl: true, languagesSpoken: ['EN'] };
 * const count = countPreferences(prefs); // Returns 3
 * ```
 */
export const countPreferences = (
  preferences: PreferencesData | null | undefined
): number => {
  if (!preferences) return 0;

  let count = 0;

  PREFERENCE_FIELDS.forEach((field) => {
    const value = preferences[field];

    if (value === true) {
      // Boolean true means preference is enabled
      count++;
    } else if (
      field === 'distance' &&
      typeof value === 'string' &&
      value !== '' &&
      value !== null
    ) {
      // Distance is a string value (e.g., "10", "25")
      count++;
    } else if (Array.isArray(value) && value && value.length > 0) {
      // Arrays (languagesSpoken, providerGender) with values
      count++;
    }
  });

  return count;
};

export interface FormattedPreferenceData {
  distance: string | null;
  virtualApptAvbl: boolean;
  onlineApptSchdlAvbl: boolean;
  evngApptAvbl: boolean;
  wkndApptAvbl: boolean;
  providerBoardCertStatus: boolean;
  providerGender: string[];
  languagesSpoken: string[];
  [key: string]: unknown;
}

/**
 * Input type for formatPreferenceData. Accepts objects with optional
 * preference fields (typically RecommendationProp or similar shapes).
 */
export interface PreferenceInputData {
  distance?: string | null;
  virtualApptAvbl?: string;
  onlineApptSchdlAvbl?: string;
  evngApptAvbl?: string;
  wkndApptAvbl?: string;
  providerBoardCertStatus?: string;
  providerGender?: string | string[];
  languagesSpoken?: string | string[];
  [key: string]: unknown;
}

/**
 * Formats preference input data into a normalized shape for backend submission.
 * Converts string flags ('y'/'n') to booleans and normalizes arrays and strings robustly.
 *
 * Handles:
 * - String flags: 'y' becomes true, anything else becomes false
 * - Empty strings and undefined: converted to null or empty arrays
 * - Arrays and comma-separated strings: properly split and normalized
 *
 * @param obj - The preference input data to format
 * @returns A normalized preference data object ready for submission
 *
 * @example
 * ```typescript
 * const raw = {
 *   distance: '5',
 *   virtualApptAvbl: 'y',
 *   languagesSpoken: 'EN,ES'
 * };
 * const formatted = formatPreferenceData(raw);
 * // Result: {
 * //   distance: '5',
 * //   virtualApptAvbl: true,
 * //   languagesSpoken: ['EN', 'ES']
 * // }
 * ```
 */
export const formatPreferenceData = (
  obj: PreferenceInputData | null | undefined
): FormattedPreferenceData => ({
  ...obj,
  distance:
    obj?.distance === '' || obj?.distance === undefined
      ? null
      : (obj?.distance as string),
  virtualApptAvbl: obj?.virtualApptAvbl === 'y',
  onlineApptSchdlAvbl: obj?.onlineApptSchdlAvbl === 'y',
  evngApptAvbl: obj?.evngApptAvbl === 'y',
  wkndApptAvbl: obj?.wkndApptAvbl === 'y',
  providerBoardCertStatus: obj?.providerBoardCertStatus === 'y',
  providerGender:
    obj?.providerGender === '' || obj?.providerGender === undefined
      ? []
      : Array.isArray(obj?.providerGender)
      ? obj.providerGender
      : (obj?.providerGender as string)?.split(',') || [],
  languagesSpoken:
    obj?.languagesSpoken === '' || obj?.languagesSpoken === undefined
      ? []
      : Array.isArray(obj?.languagesSpoken)
      ? obj.languagesSpoken
      : (obj?.languagesSpoken as string)?.split(',') || [],
});

export const formatPreferencePayload = (payload) => {
  const updatedPayload = JSON.parse(JSON.stringify(payload));

  delete updatedPayload.memberRandomizedKey;
  delete updatedPayload.createdDate;
  delete updatedPayload.lastUpdatedDate;
  delete updatedPayload.hasDefaultPreferences;

  return updatedPayload;
};

export const defaultToZero = (val: any): any =>
  val !== '' && val !== null ? val : '0';

export const removeLeadingZeroes = (str?: string): string =>
  str?.replace(/^0+/, '') ?? '';

export interface CustomPreferredPolicy {
  badgeLabel: string;
  description: string;
  icon: string;
  id: string;
  key: string;
  'lagoon.updateAt': string;
  'lagoon.updatedBy': string;
  policyId: string;
  popoverContent: string;
  productName: string;
  reciprocityId: string;
  byteCardLabel: string;
}

export const getIsCustomPreferredPolicy = (
  customPreferredPolicies: CustomPreferredPolicy[],
  policyId: string,
  reciprocityId: string
): boolean =>
  !!customPreferredPolicies?.find((cpp) => {
    if (cpp.productName === 'OBH CUSTOM NETWORK') {
      return cpp.reciprocityId === reciprocityId;
    }

    return (
      removeLeadingZeroes(cpp.policyId) === removeLeadingZeroes(policyId) &&
      cpp.reciprocityId === reciprocityId
    );
  });

export const getCustomPreferredPolicyData = (
  customPreferredPolicies: CustomPreferredPolicy[],
  policyId: string,
  reciprocityId: string
): CustomPreferredPolicy | undefined =>
  customPreferredPolicies?.find((cpp) => {
    if (cpp.productName === 'OBH CUSTOM NETWORK') {
      return cpp.reciprocityId === reciprocityId;
    }

    return (
      (!cpp.policyId ||
        removeLeadingZeroes(cpp.policyId) === removeLeadingZeroes(policyId)) &&
      cpp.reciprocityId === reciprocityId
    );
  });

export const useCustomPolicy = (
  selectedMember,
  tableFunction1
): {
  isCustomPreferredPolicy: boolean;
  customPreferredPolicyData: CustomPreferredPolicy | undefined;
} => {
  // const tableFunction1 = ;

  const data =
    tableFunction1 != null
      ? { ['custom-preferred-policies']: tableFunction1() }
      : null;
  const res = data != null ? data['custom-preferred-policies'] : [];

  const currentMember = selectedMember ?? {
    policyNumber: '',
    reciprocityId: '',
  };

  const isCustomPreferredPolicy: boolean = getIsCustomPreferredPolicy(
    res,
    currentMember?.policyNumber ?? '',
    currentMember?.reciprocityId ?? ''
  );

  const customPreferredPolicyData = getCustomPreferredPolicyData(
    res,
    currentMember?.policyNumber ?? '',
    currentMember?.reciprocityId ?? ''
  );

  return { isCustomPreferredPolicy, customPreferredPolicyData };
};

export const defaultStringToEmpty = (
  string: string | undefined | null
): string => string ?? '';

export const getAcoBenefitPlan = (
  cesProductCode?: string,
  memberMarketType?: string,
  membershipCategory?: string,
  population?: string,
  acoBenefitPlanCollection?: string
): boolean => {
  const isNotOxford = membershipCategory !== 'OXFORD';
  if (population === 'USP') {
    return isNotOxford;
  }
  const ACO_BENEFIT_COLLECTION =
    acoBenefitPlanCollection?.split(',')?.map((pair) => {
      const [marketType, productCode] = pair.split(':');
      return [marketType, productCode];
    }) ?? [];
  return ACO_BENEFIT_COLLECTION?.some(([marketType, productCode]) =>
    defaultStringToEmpty(cesProductCode).length > 0 &&
    defaultStringToEmpty(memberMarketType).length > 0
      ? productCode === cesProductCode && marketType === memberMarketType
      : false
  );
};

export const truncateString = (str: string = '', num: number): string => {
  if (num <= 0) {
    return '';
  }
  if (str.length > num) {
    return str.slice(0, num) + '...';
  } else {
    return str;
  }
};

export const distanceParser = (distance: string): string => {
  const number = parseFloat(distance);

  return isNaN(number)
    ? t('SMART_CHOICE.AUDIT_SCREEN.Number_of_miles', { number: '0' })
    : t('SMART_CHOICE.AUDIT_SCREEN.Number_of_miles', {
        number: number.toFixed(2).replace('.00', ''),
      });
};

export const getLanguageFormatted = (data: string) =>
  truncateString(
    data
      ?.split(',')
      ?.map((item) => enLangugages[item])
      ?.map((item) => removeSpecialCharactersUsingMatch(item ?? ''))
      ?.join(', ')
      ?.toString(),
    28
  );

export const formattedValue = (val: string | null | undefined): string =>
  val?.toLowerCase() === 'y'
    ? t('Yes')
    : val?.toLowerCase() === 'n'
    ? t('No')
    : t('N/A');

export const getLLMStatus = (llmStatus, t): String => {
  if (llmStatus?.toUpperCase() === 'NA') {
    return t('SMART_CHOICE.AUDIT_SCREEN.Not Applied');
  } else {
    return llmStatus === '1' ? t('Yes') : t('No');
  }
};

export const getFlagValue = (val: string | null | undefined) =>
  val === '' ? 'n' : val;

export const useSuppressPremiumSmartchoice = (state: string): string => {
  const suppressStates = useLagoon('config')()?.find(
    (row) => row.key === 'SMART_CHOICE_SUPRESS_PREMIUM'
  )?.value;

  return suppressStates
    ?.replace(/[\s,]+/g, ',')
    ?.split(',')
    ?.includes(state)
    ? 'Y'
    : 'N';
};

export const getSuppressCostEfficiencySmartChoice = (
  specialty: string | string[]
): string => {
  const suppressSpecialtiesLagoonData =
    useLagoon('suppress-smart-choice-ohpd-specialities')?.() ?? [];
  const suppressSpecialties = suppressSpecialtiesLagoonData?.flatMap((item) => [
    ...(item.medicalSpecialistsRollupCode
      ? item.medicalSpecialistsRollupCode.split(',').map((code) => code.trim())
      : []),
    ...(item.primaryCareRollupCode
      ? item.primaryCareRollupCode.split(',').map((code) => code.trim())
      : []),
  ]);
  const codes = Array.isArray(specialty)
    ? specialty
    : specialty.split(',').map((code) => code.trim());
  return suppressSpecialties.some((specialtyCode) =>
    codes.includes(specialtyCode)
  )
    ? 'N'
    : 'Y';
};
